import React, { useState, useEffect } from 'react';
import { Bell, UserPlus, Check, X, AlertCircle, Film, Tv, ChevronDown, Clock, ChevronLeft, ChevronRight, CheckCheck, XCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const DesktopNotificationsTab = ({ notifications, userId, isInline = false }) => {
  const [userShows, setUserShows] = useState([]);
  const [selectedShowId, setSelectedShowId] = useState('');
  const [showDropdownOpen, setShowDropdownOpen] = useState({});
  const [processingNotification, setProcessingNotification] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [currentCollabIndex, setCurrentCollabIndex] = useState(0);
  const [currentFriendIndex, setCurrentFriendIndex] = useState(0);
  const [collaborationRequests, setCollaborationRequests] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [showingShowSelector, setShowingShowSelector] = useState(null);
  
  useEffect(() => {
    const fetchUserShows = async () => {
      if (!userId) return;
      
      try {
        const userShowsRef = collection(db, 'users', userId, 'shows');
        const userShowsSnapshot = await getDocs(userShowsRef);
        const showsData = userShowsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUserShows(showsData);
        
        if (showsData.length > 0) {
          setSelectedShowId(showsData[0].id);
        }
      } catch (err) {
        console.error('Error fetching user shows:', err);
      }
    };
    
    fetchUserShows();
  }, [userId]);
  
  useEffect(() => {
    if (notifications) {
      // Filter collaboration notifications (both show and episode)
      const collabNotifications = notifications.filter(
        notification => notification.type === 'show_collaboration' || notification.type === 'episode_collaboration'
      );
      
      // Filter friend request notifications
      const friendNotifications = notifications.filter(
        notification => notification.type === 'friend_request'
      );
      
      // Sort by timestamp (newest first)
      const sortByTimestamp = (a, b) => {
        const timeA = a.timestamp ? (a.timestamp.toDate ? a.timestamp.toDate() : new Date(a.timestamp)) : new Date();
        const timeB = b.timestamp ? (b.timestamp.toDate ? b.timestamp.toDate() : new Date(b.timestamp)) : new Date();
        return timeB - timeA;
      };
      
      setCollaborationRequests(collabNotifications.sort(sortByTimestamp));
      setFriendRequests(friendNotifications.sort(sortByTimestamp));
    }
  }, [notifications]);
  
  // Helper functions
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };
  
  const formatTimestamp = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return diffDay === 1 ? 'Yesterday' : `${diffDay} days ago`;
    } else if (diffHour > 0) {
      return `${diffHour}h ago`;
    } else if (diffMin > 0) {
      return `${diffMin}m ago`;
    } else {
      return 'Just now';
    }
  };
  
  // Handler functions
  const handleAcceptFriendRequest = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const acceptFriendRequest = httpsCallable(functions, 'acceptFriendRequest');
      await acceptFriendRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error accepting friend request:', err);
      setError('Failed to accept friend request. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleDeclineFriendRequest = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const declineFriendRequest = httpsCallable(functions, 'declineFriendRequest');
      await declineFriendRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error declining friend request:', err);
      setError('Failed to decline friend request. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleAcceptShowCollaboration = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const acceptShowCollaborationRequest = httpsCallable(functions, 'acceptShowCollaborationRequest');
      await acceptShowCollaborationRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error accepting show collaboration:', err);
      setError('Failed to accept show collaboration. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleDeclineShowCollaboration = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const declineShowCollaborationRequest = httpsCallable(functions, 'declineShowCollaborationRequest');
      await declineShowCollaborationRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error declining show collaboration:', err);
      setError('Failed to decline show collaboration. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleAcceptEpisodeCollaboration = async (notificationId) => {
    setShowingShowSelector(notificationId);
  };
  
  const confirmEpisodeCollaboration = async (notificationId, showId) => {
    if (!showId) {
      setError('Please select a show to add this episode to');
      return;
    }
    
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const acceptEpisodeCollaborationRequest = httpsCallable(functions, 'acceptEpisodeCollaborationRequest');
      await acceptEpisodeCollaborationRequest({ 
        notificationId, 
        targetShowId: showId 
      });
      
      setShowingShowSelector(null);
      
      window.location.reload();
    } catch (err) {
      console.error('Error accepting episode collaboration:', err);
      setError('Failed to accept episode collaboration. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleDeclineEpisodeCollaboration = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const declineEpisodeCollaborationRequest = httpsCallable(functions, 'declineEpisodeCollaborationRequest');
      await declineEpisodeCollaborationRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error declining episode collaboration:', err);
      setError('Failed to decline episode collaboration. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleCancelShowCollaboration = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const cancelShowCollaborationRequest = httpsCallable(functions, 'cancelShowCollaborationRequest');
      await cancelShowCollaborationRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error canceling show collaboration:', err);
      setError('Failed to cancel show collaboration. Please try again.');
    } finally {
         setProcessingNotification(null);
    }
  };
  
  const handleCancelEpisodeCollaboration = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const cancelEpisodeCollaborationRequest = httpsCallable(functions, 'cancelEpisodeCollaborationRequest');
      await cancelEpisodeCollaborationRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error canceling episode collaboration:', err);
      setError('Failed to cancel episode collaboration. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  const handleCancelFriendRequest = async (notificationId) => {
    try {
      setProcessingNotification(notificationId);
      setError('');
      
      const functions = getFunctions();
      const declineFriendRequest = httpsCallable(functions, 'declineFriendRequest');
      await declineFriendRequest({ notificationId });
      
      window.location.reload();
    } catch (err) {
      console.error('Error canceling friend request:', err);
      setError('Failed to cancel friend request. Please try again.');
    } finally {
      setProcessingNotification(null);
    }
  };
  
  
  // Separate notifications by type
  const pendingNotifications = notifications.filter(n => n.status === 'pending');
  const otherNotifications = notifications.filter(n => n.status !== 'pending');
  
  // Navigation functions for collaboration notifications
  const nextCollab = () => {
    if (collaborationRequests.length > 0) {
      setCurrentCollabIndex((prevIndex) => 
        prevIndex === collaborationRequests.length - 1 ? 0 : prevIndex + 1
      );
    }
  };
  
  const prevCollab = () => {
    if (collaborationRequests.length > 0) {
      setCurrentCollabIndex((prevIndex) => 
        prevIndex === 0 ? collaborationRequests.length - 1 : prevIndex - 1
      );
    }
  };
  
  // Navigation functions for friend request notifications
  const nextFriend = () => {
    if (friendRequests.length > 0) {
      setCurrentFriendIndex((prevIndex) => 
        prevIndex === friendRequests.length - 1 ? 0 : prevIndex + 1
      );
    }
  };
  
  const prevFriend = () => {
    if (friendRequests.length > 0) {
      setCurrentFriendIndex((prevIndex) => 
        prevIndex === 0 ? friendRequests.length - 1 : prevIndex - 1
      );
    }
  };
  
  // Add these functions to handle scrolling with chevrons
  const scrollContainerLeft = (containerId) => {
    const container = document.getElementById(containerId);
    if (container) {
      container.scrollBy({ left: -380, behavior: 'smooth' });
    }
  };
  
  const scrollContainerRight = (containerId) => {
    const container = document.getElementById(containerId);
    if (container) {
      container.scrollBy({ left: 380, behavior: 'smooth' });
    }
  };
  
  // Update the scrollable container style to include position relative
  const scrollableContainerStyle = {
    display: 'flex',
    gap: '1rem',
    overflowX: 'auto',
    paddingBottom: '0.5rem',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    overflowY: 'hidden',
    position: 'relative',
    padding: '0rem' // Add padding for the chevrons
  };
  
  // Style for the chevron buttons
  const chevronButtonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  };
  
  // Add this useEffect to create consistent animations for both variants
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Notification content animation */
      .notification-container {
        opacity: 1;
        transform: translateY(0);
      }
      
      /* Tab item animations with shorter delay */
      .tab-item {
        opacity: 0;
        transform: translateY(10px);
      }
      
      .notification-container .tab-item {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 0.3s ease-out, transform 0.3s ease-out;
        transition-delay: calc(0.1s + (var(--item-index) * 0.05s));
      }
      
      /* Inline variant specific animations */
      .notification-container.inline .tab-item {
        transition-delay: calc(0.05s + (var(--item-index) * 0.03s));
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  if (error) {
    return (
      <div style={{
        backgroundColor: 'rgba(255, 107, 107, 0.1)',
        border: '1px solid #ff6b6b',
        borderRadius: '0.5rem',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#ff6b6b',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <AlertCircle size={20} />
        {error}
      </div>
    );
  }
  
  // If inline, return a styled version similar to other tabs
  if (isInline) {
    return (
      <div className={`notification-container inline`} style={{ width: 'calc(100% - 4rem)', color: 'white', margin: '0 auto' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Notifications</h2>
          {pendingNotifications.length > 0 && (
            <span style={{
              backgroundColor: '#6600CC',
              color: 'white',
              fontSize: '0.8rem',
              padding: '0.2rem 0.5rem',
              borderRadius: '1rem',
              fontWeight: 'bold'
            }}>
              {pendingNotifications.length} pending
            </span>
          )}
        </div>
        
        {notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
            <Bell size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
            <p>You don't have any notifications</p>
          </div>
        ) : (
          <div>
            {/* Collaboration Notifications Section */}
            {collaborationRequests.length > 0 && (
              <div style={{
                backgroundColor: 'rgba(102, 0, 204, 0.15)',
                borderRadius: '0.75rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ 
                  marginTop: '0rem',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'grey'
                }}>
                  Collaboration Notifications
                </h4>
                
                <div style={{ position: 'relative' }}>
                  <div 
                    id="inline-collab-container"
                    style={scrollableContainerStyle}
                    className="hide-scrollbar"
                  >
                    
                    {collaborationRequests.map((notification, index) => (
                      <div
                        key={notification.id}
                        className="tab-item"
                        style={{
                          '--item-index': index,
                        }}
                      >
                        <NotificationItem 
                          notification={notification}
                          userId={userId}
                          userShows={userShows}
                          selectedShowId={selectedShowId}
                          setSelectedShowId={setSelectedShowId}
                          showDropdownOpen={showDropdownOpen}
                          setShowDropdownOpen={setShowDropdownOpen}
                          processingNotification={processingNotification}
                          handleAcceptFriendRequest={handleAcceptFriendRequest}
                          handleDeclineFriendRequest={handleDeclineFriendRequest}
                          handleAcceptShowCollaboration={handleAcceptShowCollaboration}
                          handleDeclineShowCollaboration={handleDeclineShowCollaboration}
                          handleAcceptEpisodeCollaboration={handleAcceptEpisodeCollaboration}
                          handleDeclineEpisodeCollaboration={handleDeclineEpisodeCollaboration}
                          handleCancelShowCollaboration={handleCancelShowCollaboration}
                          handleCancelEpisodeCollaboration={handleCancelEpisodeCollaboration}
                          handleCancelFriendRequest={handleCancelFriendRequest}
                          navigate={navigate}
                          formatDate={formatDate}
                          formatTimestamp={formatTimestamp}
                          showingShowSelector={showingShowSelector}
                          setShowingShowSelector={setShowingShowSelector}
                          confirmEpisodeCollaboration={confirmEpisodeCollaboration}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {collaborationRequests.length > 1 && (
                    <>
                      <button 
                        onClick={() => scrollContainerLeft('inline-collab-container')}
                        style={{
                          ...chevronButtonStyle,
                          left: '-2rem'
                        }}
                      >
                        <ChevronLeft size={20} color="white" />
                      </button>
                      
                      <button 
                        onClick={() => scrollContainerRight('inline-collab-container')}
                        style={{
                          ...chevronButtonStyle,
                          right: '-2rem'
                        }}
                      >
                        <ChevronRight size={20} color="white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Friend Requests Section */}
            {friendRequests.length > 0 && (
              <div style={{
                backgroundColor: 'rgba(102, 0, 204, 0.15)',
                borderRadius: '0.75rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ 
                  margin: '0 0 .74rem 0', 
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: '#999'
                }}>
                  Friend Notification
                </h4>
                
                <div style={{ position: 'relative' }}>
                  <div 
                    id="inline-friend-container"
                    style={scrollableContainerStyle}
                    className="hide-scrollbar"
                  >
                    {friendRequests.map((notification, index) => (
                      <div
                        key={notification.id}
                        className="tab-item"
                        style={{
                          '--item-index': index,
                        }}
                      >
                        <NotificationItem 
                          notification={notification}
                          userId={userId}
                          userShows={userShows}
                          selectedShowId={selectedShowId}
                          setSelectedShowId={setSelectedShowId}
                          showDropdownOpen={showDropdownOpen}
                          setShowDropdownOpen={setShowDropdownOpen}
                          processingNotification={processingNotification}
                          handleAcceptFriendRequest={handleAcceptFriendRequest}
                          handleDeclineFriendRequest={handleDeclineFriendRequest}
                          handleAcceptShowCollaboration={handleAcceptShowCollaboration}
                          handleDeclineShowCollaboration={handleDeclineShowCollaboration}
                          handleAcceptEpisodeCollaboration={handleAcceptEpisodeCollaboration}
                          handleDeclineEpisodeCollaboration={handleDeclineEpisodeCollaboration}
                          handleCancelShowCollaboration={handleCancelShowCollaboration}
                          handleCancelEpisodeCollaboration={handleCancelEpisodeCollaboration}
                          handleCancelFriendRequest={handleCancelFriendRequest}
                          navigate={navigate}
                          formatDate={formatDate}
                          formatTimestamp={formatTimestamp}
                          showingShowSelector={showingShowSelector}
                          setShowingShowSelector={setShowingShowSelector}
                          confirmEpisodeCollaboration={confirmEpisodeCollaboration}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {friendRequests.length > 1 && (
                    <>
                      <button 
                        onClick={() => scrollContainerLeft('inline-friend-container')}
                        style={{
                          ...chevronButtonStyle,
                          left: '-2rem'
                        }}
                      >
                        <ChevronLeft size={20} color="white" />
                      </button>
                      
                      <button 
                        onClick={() => scrollContainerRight('inline-friend-container')}
                        style={{
                          ...chevronButtonStyle,
                          right: '-2rem'
                        }}
                      >
                        <ChevronRight size={20} color="white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  
  
  return (
    <div 
      className="notification-container"
      style={{
        backgroundColor: 'rgb(102, 0, 204,.9)',
        backdropFilter: 'blur(15px)',
        padding: '1.5rem',
        position: 'fixed',
        width: 'calc(100% - 3rem)',
        top: '4rem',
        zIndex: '10',
        borderBottom: '1px solid #333',
        overflowY: 'auto'
      }}
    >
      <div style={{ width: 'calc(100% - 4rem)', color: 'white', margin: '0 auto' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Notifications</h2>
          {pendingNotifications.length > 0 && (
            <span style={{
              backgroundColor: '#6600CC',
              color: 'white',
              fontSize: '0.8rem',
              padding: '0.2rem 0.5rem',
              borderRadius: '1rem',
              fontWeight: 'bold',
              marginLeft: '0.5rem'
            }}>
              {pendingNotifications.length} pending
            </span>
          )}
        </div>
        
        {notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
            <Bell size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
            <p>You don't have any notifications</p>
          </div>
        ) : (
          <div>
            {/* Collaboration Notifications Section */}
            {collaborationRequests.length > 0 && (
              <div style={{
                backgroundColor: 'rgba(102, 0, 204, 0.15)',
                borderRadius: '0.75rem',
                marginBottom: '0rem'
              }}>
                <h4 style={{ 
                  margin: '0 0 0.75rem 0', 
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#999'
                }}>
                  Collaboration Notifications
                </h4>
                
                <div style={{ position: 'relative' }}>
                  <div 
                    id="collab-container"
                    style={scrollableContainerStyle}
                    className="hide-scrollbar"
                  >
                    {collaborationRequests.map((notification, index) => (
                      <div
                        key={notification.id}
                        className="tab-item"
                        style={{
                          '--item-index': index,
                        }}
                      >
                        <NotificationItem 
                          notification={notification}
                          userId={userId}
                          userShows={userShows}
                          selectedShowId={selectedShowId}
                          setSelectedShowId={setSelectedShowId}
                          showDropdownOpen={showDropdownOpen}
                          setShowDropdownOpen={setShowDropdownOpen}
                          processingNotification={processingNotification}
                          handleAcceptFriendRequest={handleAcceptFriendRequest}
                          handleDeclineFriendRequest={handleDeclineFriendRequest}
                          handleAcceptShowCollaboration={handleAcceptShowCollaboration}
                          handleDeclineShowCollaboration={handleDeclineShowCollaboration}
                          handleAcceptEpisodeCollaboration={handleAcceptEpisodeCollaboration}
                          handleDeclineEpisodeCollaboration={handleDeclineEpisodeCollaboration}
                          handleCancelShowCollaboration={handleCancelShowCollaboration}
                          handleCancelEpisodeCollaboration={handleCancelEpisodeCollaboration}
                          handleCancelFriendRequest={handleCancelFriendRequest}
                          navigate={navigate}
                          formatDate={formatDate}
                          formatTimestamp={formatTimestamp}
                          showingShowSelector={showingShowSelector}
                          setShowingShowSelector={setShowingShowSelector}
                          confirmEpisodeCollaboration={confirmEpisodeCollaboration}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {collaborationRequests.length > 1 && (
                    <>
                      <button 
                        onClick={() => scrollContainerLeft('collab-container')}
                        style={{
                          ...chevronButtonStyle,
                          left: '-2rem'
                        }}
                      >
                        <ChevronLeft size={20} color="white" />
                      </button>
                      
                      <button 
                        onClick={() => scrollContainerRight('collab-container')}
                        style={{
                          ...chevronButtonStyle,
                          right: '-2rem'
                        }}
                      >
                        <ChevronRight size={20} color="white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Friend Requests Section */}
            {friendRequests.length > 0 && (
              <div style={{
                backgroundColor: 'rgba(102, 0, 204, 0.15)',
                borderRadius: '0.75rem',
              }}>
                <h4 style={{ 
                  margin: '0 0 0rem 0', 
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#999'
                }}>
                  Friend Notifications
                </h4>
                
                <div style={{ position: 'relative' ,}}>
                  <div 
                    id="friend-container"
                    style={scrollableContainerStyle}
                    className="hide-scrollbar"
                  >
                    {friendRequests.map((notification, index) => (
                      <div
                        key={notification.id}
                        className="tab-item"
                        style={{
                          '--item-index': index,
                        }}
                      >
                        <NotificationItem 
                          notification={notification}
                          userId={userId}
                          userShows={userShows}
                          selectedShowId={selectedShowId}
                          setSelectedShowId={setSelectedShowId}
                          showDropdownOpen={showDropdownOpen}
                          setShowDropdownOpen={setShowDropdownOpen}
                          processingNotification={processingNotification}
                          handleAcceptFriendRequest={handleAcceptFriendRequest}
                          handleDeclineFriendRequest={handleDeclineFriendRequest}
                          handleAcceptShowCollaboration={handleAcceptShowCollaboration}
                          handleDeclineShowCollaboration={handleDeclineShowCollaboration}
                          handleAcceptEpisodeCollaboration={handleAcceptEpisodeCollaboration}
                          handleDeclineEpisodeCollaboration={handleDeclineEpisodeCollaboration}
                          handleCancelShowCollaboration={handleCancelShowCollaboration}
                          handleCancelEpisodeCollaboration={handleCancelEpisodeCollaboration}
                          handleCancelFriendRequest={handleCancelFriendRequest}
                          navigate={navigate}
                          formatDate={formatDate}
                          formatTimestamp={formatTimestamp}
                          showingShowSelector={showingShowSelector}
                          setShowingShowSelector={setShowingShowSelector}
                          confirmEpisodeCollaboration={confirmEpisodeCollaboration}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {friendRequests.length > 1 && (
                    <>
                      <button 
                        onClick={() => scrollContainerLeft('friend-container')}
                        style={{
                          ...chevronButtonStyle,
                          left: '-2rem'
                        }}
                      >
                        <ChevronLeft size={20} color="white" />
                      </button>
                      
                      <button 
                        onClick={() => scrollContainerRight('friend-container')}
                        style={{
                          ...chevronButtonStyle,
                          right: '-2rem'
                        }}
                      >
                        <ChevronRight size={20} color="white" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Define NotificationItem as a separate component
const NotificationItem = ({
  notification,
  userId,
  userShows,
  selectedShowId,
  setSelectedShowId,
  showDropdownOpen,
  setShowDropdownOpen,
  processingNotification,
  handleAcceptFriendRequest,
  handleDeclineFriendRequest,
  handleAcceptShowCollaboration,
  handleDeclineShowCollaboration,
  handleCancelShowCollaboration,
  handleAcceptEpisodeCollaboration,
  handleDeclineEpisodeCollaboration,
  handleCancelEpisodeCollaboration,
  handleCancelFriendRequest,
  navigate,
  formatDate,
  formatTimestamp,
  showingShowSelector,
  setShowingShowSelector,
  confirmEpisodeCollaboration
}) => {
  const isProcessing = processingNotification === notification.id;
  const isPending = notification.status === 'pending';
  const isAccepted = notification.status === 'accepted';
  const isDeclined = notification.status === 'declined';
  const isSender = notification.senderId === userId;
  
  // Base notification card style
  const cardStyle = {
    width: '380px',
    height:' 210px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '1.5rem',
    padding: '1.5rem',
    marginBottom: '0.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    flex: '0 0 auto',
    position: 'relative'
  };
  
  // Render different notification types
  switch (notification.type) {
    case 'friend_request':
      return (
        <FriendRequestNotification 
          notification={notification}
          isPending={isPending}
          isAccepted={isAccepted}
          isDeclined={isDeclined}
          isSender={isSender}
          isProcessing={isProcessing}
          handleAcceptFriendRequest={handleAcceptFriendRequest}
          handleDeclineFriendRequest={handleDeclineFriendRequest}
          handleCancelFriendRequest={handleCancelFriendRequest}
          formatTimestamp={formatTimestamp}
          cardStyle={cardStyle}
        />
      );
      
    case 'show_collaboration':
      return (
        <ShowCollaborationNotification 
          notification={notification}
          isPending={isPending}
          isAccepted={isAccepted}
          isDeclined={isDeclined}
          isSender={isSender}
          isProcessing={isProcessing}
          handleAcceptShowCollaboration={handleAcceptShowCollaboration}
          handleDeclineShowCollaboration={handleDeclineShowCollaboration}
          handleCancelShowCollaboration={handleCancelShowCollaboration}
          formatDate={formatDate}
          formatTimestamp={formatTimestamp}
          cardStyle={cardStyle}
        />
      );
      
    case 'episode_collaboration':
      return (
        <EpisodeCollaborationNotification 
          notification={notification}
          isPending={isPending}
          isAccepted={isAccepted}
          isDeclined={isDeclined}
          isSender={isSender}
          isProcessing={isProcessing}
          handleAcceptEpisodeCollaboration={handleAcceptEpisodeCollaboration}
          handleDeclineEpisodeCollaboration={handleDeclineEpisodeCollaboration}
          handleCancelEpisodeCollaboration={handleCancelEpisodeCollaboration}
          formatDate={formatDate}
          formatTimestamp={formatTimestamp}
          cardStyle={cardStyle}
          showingShowSelector={showingShowSelector}
          userShows={userShows}
          confirmEpisodeCollaboration={confirmEpisodeCollaboration}
          selectedShowId={selectedShowId}
          setSelectedShowId={setSelectedShowId}
          setShowingShowSelector={setShowingShowSelector}
        />
      );
      
    default:
      return (
        <div style={cardStyle}>
          <p>Unknown notification type</p>
        </div>
      );
  }
};

// Friend Request Notification Component

// Friend Request Notification Component
const FriendRequestNotification = ({
  notification,
  isPending,
  isAccepted,
  isDeclined,
  isSender,
  isProcessing,
  handleAcceptFriendRequest,
  handleDeclineFriendRequest,
  handleCancelFriendRequest,
  formatTimestamp,
  cardStyle
}) => {
  // Create a modified style for friend requests with 70px height
  const friendRequestCardStyle = {
    ...cardStyle,
    height: '40px', 
    width: '200px',
    padding: '1rem',
    borderRadius: '1rem'
  };

  return (
    <div style={friendRequestCardStyle}>
      {isPending && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#6600CC'
        }} />
      )}
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '0.75rem'
      }}>
        <div>
        <h4 style={{ 
    margin: '0 0 0.5rem 0', 
    fontSize: '.8rem',
    color: 'grey', 
    width: '200px',
    fontWeight: '700',
    display: 'flex'
      }}>
       Friend Request 
       {notification.timestamp && (
        <p style={{ 
          marginTop: '0px',
          marginLeft: 'auto',
          fontSize: '0.7rem',
          color: '#777',
          textAlign: 'right'
        }}>
          {notification.timestamp.toDate ? 
            formatTimestamp(notification.timestamp.toDate()) : 
            formatTimestamp(new Date(notification.timestamp))}
        </p>
      )}
      </h4>
      
          <div style={{display: 'flex', marginTop: '-10px'}}>
          <p style={{ 
            margin: 0,
            fontSize: '0.8rem',
            color: 'white',
            fontWeight: '600'
          }}>
            {isSender 
              ? `${notification.receiverName}`
              : `${notification.senderName} `
            }
            
          </p>
              {!isPending && (
        <div style={{
        color: 'grey', marginLeft: 'auto'
        }}>
          {isAccepted ? <Check size={16}/> : <XCircle size={16}/>}
        </div>
      )}
      </div>
        </div>
      </div>
      
      {isPending && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          position: 'absolute',
          width: '4rem',
          bottom: '-.5rem', right: '-.5rem',
          marginTop: '0.75rem'
        }}>
          {!isSender && (
            <button
              onClick={() => handleAcceptFriendRequest(notification.id)}
              disabled={isProcessing}
              style={{
                flex: 1,
                padding: '0.5rem',
                backgroundColor: '#6600CC',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: isProcessing ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                fontSize: '0.8rem'
              }}
            >
              {isProcessing ? 'Processing...' : (
                <>
                  <Check size={14} />
                  Accept
                </>
              )}
            </button>
          )}
          
          <button
            onClick={() => isSender 
              ? handleCancelFriendRequest(notification.id) 
              : handleDeclineFriendRequest(notification.id)
            }
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: '0.5rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: isProcessing ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              fontSize: '0.8rem'
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
      
    
      
 
    </div>
  );
};
// Show Collaboration Notification Component
const ShowCollaborationNotification = ({
  notification,
  isPending,
  isAccepted,
  isDeclined,
  isSender,
  isProcessing,
  handleAcceptShowCollaboration,
  handleDeclineShowCollaboration,
  handleCancelShowCollaboration,
  formatDate,
  formatTimestamp,
  cardStyle
}) => {
  return (
    <div style={cardStyle}>
      {/* Pending indicator dot */}
      {isPending && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#6600CC'
        }} />
      )}
      <div style={{display: 'flex'}}>
      {/* Show image */}
      {notification.showImage && (
        <div style={{
          aspectRatio: '2/3',
          width: '125px',
          marginBottom: '0.75rem',
          marginRight: '0.75rem',
          borderRadius: '0.25rem',
          overflow: 'hidden'
        }}>
          <img 
            src={notification.showImage} 
            alt={notification.showName || "Show"}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>
      )}
      <div style={{width: '375px', position: 'relative'}}>
      <h4 style={{ 
    margin: '0 0 0.5rem 0', 
    fontSize: '.8rem',
    color: 'grey', 
    fontWeight: '700',
    display: 'flex'
      }}>
       Show Collab
        <p style={{marginLeft: 'auto', marginTop: '-.0rem', fontWeight: '700'}}>{formatDate(notification.timestamp)}</p>
      </h4>

      {isPending && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#6600CC'
        }} />
      )}
      
   
  <div style={{}}>

      
      {/* Show name */}
      <div style={{
        fontSize: '1.3rem',
        fontWeight: 'bold',
        marginTop: '-1rem',
        
        color: 'white',
        marginBottom: '1.5rem'
      }}>
        {notification.showName || "Untitled Show"}
      </div>
   
      {/* Description */}
      {notification.showDescription && (
        <p style={{ 
          margin: '0 0 0.75rem 0',
          fontSize: '0.8rem',
          color: '#bbb',
          position: 'absolute',
          bottom: '.5rem' ,
          lineHeight: '1.3'
        }}>
          {notification.showDescription}
        </p>
      )}
      
      </div>
      
      </div>
      
      {/* Date */}
 
      </div>
      <div style={{position: 'absolute', width: 'calc(100% - 3rem', bottom: '1rem'}}>
      <h1 style={{fontSize: '.7rem', fontWeight: '600', color: 'grey'}}>  {isSender 
          ? `Sent To ${notification.receiverName}` 
          : ` From  ${notification.senderName}`
        } </h1>
      {/* Action buttons for pending notifications */}
      {isPending && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '0.75rem'
        }}>
          {!isSender && (
            <button
              onClick={() => handleAcceptShowCollaboration(notification.id)}
              disabled={isProcessing}
              style={{
                flex: 1,
                padding: '0.5rem',
                backgroundColor: '#6600CC',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: isProcessing ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                fontSize: '0.8rem'
              }}
            >
              {isProcessing ? 'Processing...' : (
                <>
                  <Check size={14} />
                  Accept
                </>
              )}
            </button>
          )}
          
          <button
            onClick={() => isSender 
              ? handleCancelShowCollaboration(notification.id) 
              : handleDeclineShowCollaboration(notification.id)
            }
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: '0.5rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: isProcessing ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              fontSize: '0.8rem'
            }}
          >
            <X size={14} />
            {isSender ? 'Cancel' : 'Decline'}
          </button>
        </div>
      )}
      
      {/* Status badge for non-pending notifications */}
      {!isPending && (
        <div style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          marginTop: '0.5rem',
          backgroundColor: isAccepted ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 107, 107, 0.1)',
          color: isAccepted ? '#4caf50' : '#ff6b6b'
        }}>
          {isAccepted ? 'Accepted' : 'Declined'}
        </div>
      )}
    </div> </div>
  );
};

// Episode Collaboration Notification Component
const EpisodeCollaborationNotification = ({
  notification,
  isPending,
  isAccepted,
  isDeclined,
  isSender,
  isProcessing,
  handleAcceptEpisodeCollaboration,
  handleDeclineEpisodeCollaboration,
  handleCancelEpisodeCollaboration,
  formatDate,
  formatTimestamp,
  cardStyle,
  showingShowSelector,
  userShows,
  confirmEpisodeCollaboration,
  selectedShowId,
  setSelectedShowId,
  setShowingShowSelector
}) => {
  return (
    <div style={cardStyle}>
      {/* Pending indicator dot */}
      {isPending && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#6600CC'
        }} />
      )}
      <div style={{display: 'flex'}}>
      {/* Episode image */}
      {notification.image && (
        <div style={{
          aspectRatio: '3/2',
          width: '150px',
          marginBottom: '0.75rem',
          marginRight: '0.75rem',
          borderRadius: '0.25rem',
          overflow: 'hidden'
        }}>
          <img 
            src={notification.image} 
            alt={notification.title || "Episode"}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>
      )}
      <div style={{position: 'relative', width: '300px'}}>
      {/* Title */}
      <h4 style={{ 
        margin: '0 0 0.5rem 0', 
        fontSize: '.8rem',
        color: 'grey', 
        fontWeight: '700',
        display: 'flex'
      }}>
      Episode Collab
        <p style={{marginLeft: 'auto', marginTop: '-.0rem', fontWeight: '700'}}>{formatDate(notification.timestamp)}</p>
      </h4>


    
      
      {/* Episode title */}
      <div style={{
        fontSize: '1.1rem',
        marginTop: '-1.1rem',
        fontWeight: 'bold',
        lineHeight: '1',
        color: 'white',
        marginBottom: '0.25rem'
      }}>
        {notification.title || "Untitled Episode"} 
      </div>
      
      {/* Episode details */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        position: 'absolute',
        bottom: '.5rem' ,
        gap: '0.75rem',
        margin: '0.5rem 0',
        fontWeight: '600',
        fontSize: '0.75rem',
        color: 'grey'
      }}>
          <span style={{  }}>{notification.duration || 22}m</span>
          
          <span style={{marginLeft: '.5rem' }}>{formatDate(notification.timestamp)}</span>
   
   
        
       
     </div>

     </div>
      </div>
      {/* Description */}
      {notification.description && (
        <p style={{ 
          margin: '0.5rem 0 0.75rem 0',
          fontSize: '0.8rem',
          color: '#bbb',
          lineHeight: '1.3'
        }}>
           {notification.description}
        </p>
      )}
      <div style={{position: 'absolute', width: 'calc(100% - 3rem', bottom: '1rem'}}>
      {/* Scene previews if available */}
      <h1 style={{fontSize: '.7rem', fontWeight: '600', color: 'grey'}}>  {isSender 
          ? `Sent To ${notification.receiverName}` 
          : ` From  ${notification.senderName}`
        } </h1>
      
      {/* Action buttons for pending notifications */}
      {isPending && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '0.75rem'
        }}>
          {!isSender && (
            <>
              {showingShowSelector === notification.id ? (
                <div style={{
                  width: '100%',
                  marginBottom: '0.75rem'
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    color: '#ccc',
                    marginBottom: '0.5rem'
                  }}>
                    Select a show to add this episode to:
                  </label>
                  
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    <select
                      value={selectedShowId}
                      onChange={(e) => setSelectedShowId(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#333',
                        color: 'white',
                        border: '1px solid #444',
                        borderRadius: '0.25rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="" disabled>Select a show</option>
                      {userShows.map(show => (
                        <option key={show.id} value={show.id}>
                          {show.showName}
                        </option>
                      ))}
                    </select>
                    
                    <button
                      onClick={() => confirmEpisodeCollaboration(notification.id, selectedShowId)}
                      disabled={!selectedShowId || isProcessing}
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: !selectedShowId ? '#444' : '#6600CC',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: !selectedShowId || isProcessing ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      {isProcessing ? 'Processing...' : 'Confirm'}
                    </button>
                    
                    <button
                      onClick={() => setShowingShowSelector(null)}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: 'transparent',
                        color: '#999',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => handleAcceptEpisodeCollaboration(notification.id)}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: '#6600CC',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: isProcessing ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    fontSize: '0.8rem'
                  }}
                >
                  {isProcessing ? 'Processing...' : (
                    <>
                      <Check size={14} />
                      Accept
                    </>
                  )}
                </button>
              )}
            </>
          )}
          
          {/* Only show decline/cancel button if show selector is not open */}
          {(isSender || !showingShowSelector || showingShowSelector !== notification.id) && (
            <button
              onClick={() => isSender 
                ? handleCancelEpisodeCollaboration(notification.id) 
                : handleDeclineEpisodeCollaboration(notification.id)
              }
              disabled={isProcessing}
              style={{
                flex: 1,
                padding: '0.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: isProcessing ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                fontSize: '0.8rem'
              }}
            >
              <X size={14} />
              {isSender ? 'Cancel' : 'Decline'}
            </button>
          )}
        </div>
      )}
      
      {/* Status badge for non-pending notifications */}
      {!isPending && (
        <div style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          marginTop: '0.5rem',
          backgroundColor: isAccepted ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 107, 107, 0.1)',
          color: isAccepted ? '#4caf50' : '#ff6b6b'
        }}>
          {isAccepted ? 'Accepted' : 'Declined'}
        </div>
      )}
    </div>
    </div>
  );
};

export default DesktopNotificationsTab; 